/*
  COMPONENT: Theme Runtime

  Owns:
  - Global initialization registry
  - Theme Editor lifecycle helpers
  - Focus trapping helpers
  - Header/mobile drawer behavior
  - Lightweight performance helpers for UI enhancement

  Does not own:
  - SEO/GEO metadata or JSON-LD
  - Cart state
  - Product variant state
  - Section-specific animation layouts
*/

(() => {
  document.documentElement.classList.add('js');
  document.documentElement.classList.remove('no-js');

  const registry = new Map();
  const cleanupRegistry = new WeakMap();

  function register(name, initializer) {
    if (typeof initializer !== 'function') return;
    registry.set(name, initializer);
    initializer(document);
  }

  function initialize(root = document) {
    registry.forEach((initializer) => initializer(root));
  }

  function setCleanup(element, cleanup) {
    const existing = cleanupRegistry.get(element);
    if (existing) existing.forEach((callback) => callback());
    cleanupRegistry.set(element, new Set([cleanup]));
  }

  function registerCleanup(element, cleanupCallback) {
    if (!element || typeof cleanupCallback !== 'function') return;
    const callbacks = cleanupRegistry.get(element) || new Set();
    callbacks.add(cleanupCallback);
    cleanupRegistry.set(element, callbacks);
  }

  function cleanup(root = document) {
    const elements = [root, ...root.querySelectorAll('*')];
    elements.forEach((element) => {
      const callbacks = cleanupRegistry.get(element);
      if (!callbacks) return;
      callbacks.forEach((callback) => callback());
      cleanupRegistry.delete(element);
    });
  }

  function getFocusable(container) {
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hasAttribute('hidden') && element.offsetParent !== null);
  }

  function trapFocus(container, event) {
    if (event.key !== 'Tab') return;
    const focusable = getFocusable(container);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function initializeDisclosure(root = document) {
    root.querySelectorAll('[data-disclosure]').forEach((disclosure) => {
      if (disclosure.dataset.initialized === 'true') return;
      disclosure.dataset.initialized = 'true';

      const trigger = disclosure.querySelector('[data-disclosure-trigger]');
      const panel = disclosure.querySelector('[data-disclosure-panel]');
      const closeButtons = disclosure.querySelectorAll('[data-disclosure-close]');
      if (!trigger || !panel) return;

      const open = () => {
        disclosure.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        panel.removeAttribute('hidden');
        document.body.classList.add('drawer-open');
        const first = getFocusable(panel)[0];
        if (first) first.focus();
      };

      const close = () => {
        disclosure.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
        panel.setAttribute('hidden', '');
        document.body.classList.remove('drawer-open');
        trigger.focus();
      };

      trigger.addEventListener('click', () => {
        if (disclosure.classList.contains('is-open')) {
          close();
        } else {
          open();
        }
      });

      closeButtons.forEach((button) => button.addEventListener('click', close));

      panel.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') close();
        trapFocus(panel, event);
      });
    });
  }

  function initializeHeader(root = document) {
    root.querySelectorAll('[data-site-header]').forEach((header) => {
      if (header.dataset.initialized === 'true') return;
      header.dataset.initialized = 'true';

      let ticking = false;
      const update = () => {
        const currentY = window.scrollY;
        header.classList.toggle('is-scrolled', currentY > 12);
      };
      const requestUpdate = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          ticking = false;
          update();
        });
      };

      update();
      window.addEventListener('scroll', requestUpdate, { passive: true });
      setCleanup(header, () => window.removeEventListener('scroll', requestUpdate));
    });
  }

  function initializeShare(root = document) {
    root.querySelectorAll('[data-share-button]').forEach((button) => {
      if (button.dataset.initialized === 'true') return;
      button.dataset.initialized = 'true';

      button.addEventListener('click', async () => {
        const shareData = {
          title: button.dataset.shareTitle || document.title,
          url: button.dataset.shareUrl || window.location.href,
        };

        if (navigator.share) {
          await navigator.share(shareData).catch(() => {});
          return;
        }

        await navigator.clipboard?.writeText(shareData.url).catch(() => {});
      });
    });
  }

  function loadLucideIcons() {
    return Promise.resolve();
  }

  function initializeLucideIcons(root = document) {
    if (!root.querySelector) return;
  }

  const fitTextElements = new Set();
  let fitTextObserver = null;
  let fitTextQueued = false;

  function getFitTextFrame(element) {
    const selector = element.dataset.fitFrame;
    if (selector) {
      const frame = element.closest(selector);
      if (frame) return frame;
    }
    return element.parentElement || element;
  }

  function fitTextElement(element) {
    if (!element.isConnected) return;

    element.style.removeProperty('font-size');

    const computedStyle = window.getComputedStyle(element);
    const baseSize = parseFloat(computedStyle.fontSize);
    const minSize = parseFloat(element.dataset.fitMin || '24');
    const frame = getFitTextFrame(element);
    const availableWidth = Math.min(element.clientWidth || Infinity, frame.clientWidth || Infinity);

    if (!baseSize || !availableWidth || availableWidth === Infinity) return;

    let size = baseSize;
    for (let attempt = 0; attempt < 8 && element.scrollWidth > element.clientWidth + 1 && size > minSize; attempt += 1) {
      const ratio = Math.max(0.1, element.clientWidth / element.scrollWidth);
      size = Math.max(minSize, size * ratio * 0.98);
      element.style.fontSize = `${size}px`;
    }
  }

  function fitTextAll() {
    fitTextQueued = false;
    fitTextElements.forEach((element) => {
      if (!element.isConnected) {
        fitTextElements.delete(element);
        return;
      }
      fitTextElement(element);
    });
  }

  function queueFitText() {
    if (fitTextQueued) return;
    fitTextQueued = true;
    window.requestAnimationFrame(fitTextAll);
  }

  function initializeFitText(root = document) {
    if (!root.querySelectorAll) return;

    if (!fitTextObserver && 'ResizeObserver' in window) {
      fitTextObserver = new ResizeObserver(queueFitText);
    }

    root.querySelectorAll('[data-fit-text]').forEach((element) => {
      if (element.dataset.fitInitialized === 'true') return;
      element.dataset.fitInitialized = 'true';
      fitTextElements.add(element);
      const frame = getFitTextFrame(element);
      if (fitTextObserver) fitTextObserver.observe(frame);
      registerCleanup(element, () => {
        fitTextElements.delete(element);
        if (fitTextObserver) fitTextObserver.unobserve(frame);
      });
    });

    queueFitText();
  }

  window.CurtisTheme = window.CurtisTheme || {};
  window.CurtisTheme.register = register;
  window.CurtisTheme.initialize = initialize;
  window.CurtisTheme.cleanup = cleanup;
  window.CurtisTheme.registerCleanup = registerCleanup;
  window.CurtisTheme.getFocusable = getFocusable;
  window.CurtisTheme.trapFocus = trapFocus;
  window.CurtisTheme.loadLucideIcons = loadLucideIcons;
  window.CurtisTheme.initializeLucideIcons = initializeLucideIcons;
  window.CurtisTheme.fitText = fitTextAll;
  window.CurtisTheme.routes = window.CurtisTheme.routes || {
    root: window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/',
  };

  register('disclosure', initializeDisclosure);
  register('fit-text', initializeFitText);
  register('header', initializeHeader);
  register('lucide-icons', initializeLucideIcons);
  register('share', initializeShare);

  if (document.fonts?.ready) document.fonts.ready.then(queueFitText).catch(() => {});
  window.addEventListener('load', queueFitText, { once: true });
  window.addEventListener('resize', queueFitText, { passive: true });

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => cleanup(event.target));
})();
